import React, { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { motion } from 'framer-motion';
import {
  CheckCircle2, XCircle, ArrowRight, ShieldCheck, Cpu, Workflow,
  Mic, Mail, Video, Database, AlertTriangle, Calculator, Sparkles, Quote
} from 'lucide-react';

type Tool = {
  name: string;
  category: string;
  bestFor: string;
  strengths: string[];
  watchOuts: string[];
  pricing: string;
  verdict: string;
};

const tools: Tool[] = [
  {
    name: 'NoQ5',
    category: 'Autonomous CRM (AI Chief of Staff)',
    bestFor: 'Revenue leaders who want CRM hygiene solved without forcing reps to type.',
    strengths: [
      'Captures from calls, meetings and email threads automatically',
      'Drafts contact, deal and activity updates for one-tap approval',
      'Writes back to HubSpot, Salesforce and Pipedrive natively',
      'EU-hosted, GDPR-compliant, AES-256-GCM, full audit trail',
    ],
    watchOuts: [
      'Single tier at €59 per user per month, annual commitment only',
      'Microsoft 365 in beta; LinkedIn on the roadmap',
    ],
    pricing: '€59 / user / month, annual',
    verdict: 'The execution layer. Built for CROs who measure success in pipeline accuracy and forecast confidence, not seat licenses.',
  },
  {
    name: 'Pipedrive AI Sales Assistant',
    category: 'Native CRM assistant',
    bestFor: 'Existing Pipedrive customers who want lightweight nudges inside the CRM.',
    strengths: [
      'Bundled inside Pipedrive with no extra contract',
      'Surfaces deal recommendations and email suggestions',
    ],
    watchOuts: [
      'Locked to Pipedrive only',
      'Suggestions only, no autonomous capture or write-back',
    ],
    pricing: 'Included with Pipedrive plans',
    verdict: 'A copilot inside Pipedrive. Useful if you live there; not a substitute for autonomous capture.',
  },
  {
    name: 'HubSpot Breeze',
    category: 'Native CRM assistant',
    bestFor: 'HubSpot-centric teams running marketing, content and basic sales workflows.',
    strengths: [
      'Tight integration with HubSpot Marketing Hub and Content Hub',
      'Strong content generation and email drafting',
    ],
    watchOuts: [
      'Limited to HubSpot data only',
      'Forecasting and pipeline evidence remain weak',
    ],
    pricing: 'Tiered with HubSpot Hubs (often Pro and above)',
    verdict: 'Excellent inside HubSpot for marketing assistance. Sales execution still depends on rep input.',
  },
  {
    name: 'Salesforce Einstein / Agentforce',
    category: 'Enterprise AI agent platform',
    bestFor: 'Large enterprises with mature Salesforce orgs and a dedicated admin team.',
    strengths: [
      'Deep Salesforce data model integration',
      'Configurable agents for service, sales and ops',
    ],
    watchOuts: [
      'Heavy implementation cost and admin overhead',
      'Per-conversation pricing can scale unpredictably',
      'Locked to Salesforce ecosystem',
    ],
    pricing: 'Add-on plus per-conversation usage',
    verdict: 'Powerful if you already standardised on Salesforce and have admins to run it. Slow to value for everyone else.',
  },
  {
    name: 'Apollo AI',
    category: 'Prospecting and outbound assistant',
    bestFor: 'SDR teams running high-volume outbound from Apollo data.',
    strengths: [
      'Large B2B contact database',
      'Sequence drafting and lead scoring',
    ],
    watchOuts: [
      'Optimised for outbound, not pipeline execution',
      'CRM updates remain manual or shallow',
    ],
    pricing: 'Per seat plus credits',
    verdict: 'Strong top-of-funnel tool. Pair with an execution layer for the rest of the funnel.',
  },
  {
    name: 'Artisan',
    category: 'AI SDR (autonomous outbound)',
    bestFor: 'Teams testing fully autonomous outbound prospecting.',
    strengths: [
      'End-to-end SDR persona that researches and emails',
      'Built-in data and deliverability tooling',
    ],
    watchOuts: [
      'Outbound-only, no CRM hygiene or deal execution',
      'Reputation risk if not carefully tuned',
    ],
    pricing: 'Per AI SDR seat',
    verdict: 'A bet on autonomous outbound. Not a replacement for revenue execution across the whole funnel.',
  },
  {
    name: 'Highspot AI',
    category: 'Sales enablement assistant',
    bestFor: 'Enablement leaders coaching reps and managing playbooks.',
    strengths: [
      'Content recommendations in the flow of work',
      'Conversation analytics and coaching cues',
    ],
    watchOuts: [
      'Enablement-first, not CRM execution',
      'Requires existing content library to shine',
    ],
    pricing: 'Enterprise quote',
    verdict: 'Best paired with a CRM execution layer. Great for content; not where pipeline gets fixed.',
  },
  {
    name: 'Salesloft / Outreach AI',
    category: 'Sales engagement assistant',
    bestFor: 'Engagement-heavy teams running cadences and dialer workflows.',
    strengths: [
      'Strong cadence orchestration',
      'AI summaries and next-step suggestions on calls',
    ],
    watchOuts: [
      'Engagement-centric; CRM stays the source of truth and stays stale',
      'Pricing climbs quickly with seats and add-ons',
    ],
    pricing: 'Per seat, enterprise quote',
    verdict: 'Strong for cadence operations. The CRM data problem still lives downstream.',
  },
];

const checklist = [
  { good: true, label: 'Captures from voice, email and meetings without rep effort' },
  { good: true, label: 'Writes back to your CRM (HubSpot, Salesforce, Pipedrive) with approvals' },
  { good: true, label: 'Produces evidence-backed pipeline and forecast signals' },
  { good: true, label: 'GDPR-compliant, EU data residency, encryption at rest and in transit' },
  { good: true, label: 'Single, predictable per-seat price, no per-conversation surprises' },
  { good: false, label: 'Locked to one CRM ecosystem' },
  { good: false, label: 'Suggestions only — still requires reps to type updates' },
  { good: false, label: 'Hidden usage-based fees that explode at scale' },
];

const BestAISalesAssistants2026 = () => {
  const [seats, setSeats] = useState(15);
  const [hoursPerRep, setHoursPerRep] = useState(6);
  const fullyLoadedRate = 95; // EUR/hour
  const weeklyHoursSaved = seats * hoursPerRep;
  const annualHours = weeklyHoursSaved * 46; // working weeks
  const annualValue = Math.round((annualHours * fullyLoadedRate) / 1000) * 1000;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Best AI Sales Assistants 2026: Honest Buyer\'s Guide',
      description:
        'An independent 2026 buyer\'s guide to AI sales assistants. Categories, strengths, watch-outs and how to pick the right execution layer for HubSpot, Salesforce or Pipedrive.',
      author: { '@type': 'Organization', name: 'NoQ5' },
      publisher: { '@type': 'Organization', name: 'NoQ5', url: 'https://noq5.ai' },
      datePublished: '2026-04-01',
      dateModified: '2026-05-13',
      mainEntityOfPage: 'https://noq5.ai/best-ai-sales-assistants-2026',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: tools.map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: t.name,
        description: t.verdict,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is an AI sales assistant?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'An AI sales assistant is software that helps revenue teams capture, structure and act on sales activity. The strongest category goes beyond suggestions and autonomously updates the CRM after rep approval, turning calls, meetings and emails into clean pipeline data.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between an AI copilot and an autonomous AI assistant?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A copilot suggests; the rep still types. An autonomous assistant captures, drafts and writes back to the CRM with one-tap approval. Copilots reduce friction; autonomous assistants change whether CRM data exists at all.',
          },
        },
        {
          '@type': 'Question',
          name: 'Which AI sales assistant works with HubSpot, Salesforce and Pipedrive?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'NoQ5 ships native integrations with HubSpot, Salesforce and Pipedrive. It writes contact, deal and activity updates back to whichever CRM you use, with full audit trail.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much does an AI sales assistant cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Pricing ranges from bundled (Pipedrive, HubSpot Breeze) to enterprise per-conversation models (Salesforce Agentforce). NoQ5 sells at a single tier of €59 per user per month billed annually with no usage surprises.',
          },
        },
      ],
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Best AI Sales Assistants 2026 | Honest Buyer's Guide"
        description="Independent 2026 buyer's guide to AI sales assistants. Compare 8 tools across CRMs, prospecting, enablement and engagement. Pick the right execution layer."
        canonicalUrl="https://noq5.ai/best-ai-sales-assistants-2026"
        keywords="best ai sales assistant, ai sales assistant 2026, ai sales assistant software, ai for sales, ai sales tools, ai sales agent, sales ai, ai sales platform, ai sales automation, ai sales assistant comparison, ai sales assistant for hubspot, ai sales assistant for salesforce, ai sales assistant for pipedrive"
        type="article"
        jsonLd={jsonLd}
      />

      <article className="bg-background">
        {/* Hero */}
        <section className="relative w-full pt-20 md:pt-28 pb-16 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: '64px 64px'
          }} />
          <div className="container relative z-10 px-4 md:px-6 max-w-5xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Buyer's Guide · Updated May 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6 text-foreground">
              The Best AI Sales Assistants of 2026
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mb-8">
              Most "AI sales assistants" are copilots that suggest. A few actually execute. This guide separates the two, ranks 8 tools across four categories, and shows revenue leaders how to pick the layer that finally fixes CRM hygiene and forecast accuracy.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" /> Independent</span>
              <span>·</span>
              <span>8 tools reviewed</span>
              <span>·</span>
              <span>HubSpot, Salesforce, Pipedrive coverage</span>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="w-full py-12 bg-muted/30">
          <div className="container px-4 md:px-6 max-w-5xl mx-auto">
            <div className="bg-card border border-border rounded-2xl p-8">
              <h2 className="text-xl font-bold mb-4 text-foreground">The short version</h2>
              <ul className="space-y-3 text-base text-foreground/90 leading-relaxed">
                <li className="flex gap-3"><span className="text-primary font-bold">1.</span><span><strong>If you want CRM hygiene solved across HubSpot, Salesforce or Pipedrive:</strong> use an autonomous CRM execution layer like <Link to="/crm-agent" className="text-primary underline underline-offset-2">NoQ5</Link>.</span></li>
                <li className="flex gap-3"><span className="text-primary font-bold">2.</span><span><strong>If you live inside one CRM and want gentle nudges:</strong> the native assistants (Pipedrive AI, HubSpot Breeze, Einstein) are good enough.</span></li>
                <li className="flex gap-3"><span className="text-primary font-bold">3.</span><span><strong>If you need outbound volume:</strong> Apollo or Artisan. Pair with an execution layer downstream.</span></li>
                <li className="flex gap-3"><span className="text-primary font-bold">4.</span><span><strong>If your problem is enablement or cadence:</strong> Highspot, Salesloft or Outreach. Useful but adjacent.</span></li>
              </ul>
            </div>
          </div>
        </section>

        {/* Methodology */}
        <section className="w-full py-16">
          <div className="container px-4 md:px-6 max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">How we picked the categories</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-3xl">
              "AI sales assistant" is a crowded label. Four meaningfully different categories sit underneath it, each solving a different revenue problem.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: Cpu, title: 'Autonomous CRM (AI Chief of Staff)', desc: 'Captures activity, drafts CRM updates, writes back with approval. Closest thing to "set and forget" pipeline hygiene.' },
                { icon: Database, title: 'Native CRM assistants', desc: 'Built into HubSpot, Salesforce or Pipedrive. Suggests next steps; does not replace data entry.' },
                { icon: Workflow, title: 'Prospecting / AI SDRs', desc: 'Top-of-funnel research, contact data, outbound drafting and sending.' },
                { icon: Video, title: 'Enablement and engagement', desc: 'Coaches reps, surfaces content, orchestrates cadences. Adjacent to execution, not a substitute.' },
              ].map((c) => (
                <div key={c.title} className="bg-card border border-border rounded-xl p-6">
                  <c.icon className="w-6 h-6 text-primary mb-3" />
                  <h3 className="text-lg font-bold mb-2 text-foreground">{c.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tool reviews */}
        <section className="w-full py-16 bg-muted/30">
          <div className="container px-4 md:px-6 max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-foreground">8 AI sales assistants, reviewed</h2>
            <div className="space-y-6">
              {tools.map((tool, i) => (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.4 }}
                  className={`bg-card border rounded-2xl p-6 md:p-8 ${i === 0 ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border'}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-2xl font-bold text-foreground">{i + 1}. {tool.name}</h3>
                        {i === 0 && (
                          <span className="text-xs font-bold uppercase tracking-wide bg-primary text-primary-foreground px-2.5 py-1 rounded-full">Top pick</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{tool.category}</p>
                    </div>
                    <span className="text-sm font-semibold text-foreground bg-secondary px-3 py-1.5 rounded-lg">{tool.pricing}</span>
                  </div>

                  <p className="text-base text-foreground/90 mb-5 leading-relaxed"><strong className="text-foreground">Best for:</strong> {tool.bestFor}</p>

                  <div className="grid md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-primary mb-2">Strengths</p>
                      <ul className="space-y-2">
                        {tool.strengths.map((s) => (
                          <li key={s} className="flex gap-2 text-sm text-foreground/90">
                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Watch-outs</p>
                      <ul className="space-y-2">
                        {tool.watchOuts.map((w) => (
                          <li key={w} className="flex gap-2 text-sm text-foreground/90">
                            <AlertTriangle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <span>{w}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="border-t border-border pt-4">
                    <p className="text-sm text-foreground/90 leading-relaxed"><strong className="text-foreground">Verdict:</strong> {tool.verdict}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Buyer's checklist */}
        <section className="w-full py-16">
          <div className="container px-4 md:px-6 max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">The 2026 buyer's checklist</h2>
            <p className="text-lg text-muted-foreground mb-10 max-w-3xl leading-relaxed">
              Bring this to the vendor demo. If a tool fails three or more, it is not the execution layer; it is a feature.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {checklist.map((item) => (
                <div key={item.label} className="flex items-start gap-3 bg-card border border-border rounded-xl p-4">
                  {item.good ? (
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  )}
                  <span className="text-sm text-foreground leading-relaxed">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI estimator */}
        <section className="w-full py-16 bg-muted/30">
          <div className="container px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Calculator className="w-10 h-10 text-primary mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">What the right assistant is worth</h2>
              <p className="text-lg text-muted-foreground">Estimate the value of giving each rep their selling time back.</p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-8 space-y-8">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-foreground">Sales reps on the team</label>
                  <span className="text-2xl font-bold text-primary">{seats}</span>
                </div>
                <Slider value={[seats]} onValueChange={(v) => setSeats(v[0])} min={3} max={100} step={1} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-foreground">Hours per rep per week on CRM admin</label>
                  <span className="text-2xl font-bold text-primary">{hoursPerRep}h</span>
                </div>
                <Slider value={[hoursPerRep]} onValueChange={(v) => setHoursPerRep(v[0])} min={2} max={15} step={1} />
              </div>

              <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
                <div className="text-center">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Hours / week</p>
                  <p className="text-2xl font-bold text-foreground">{weeklyHoursSaved}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Hours / year</p>
                  <p className="text-2xl font-bold text-foreground">{annualHours.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Annual value</p>
                  <p className="text-2xl font-bold text-primary">€{annualValue.toLocaleString()}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">Based on a fully-loaded rep cost of €95 / hour and 46 working weeks per year.</p>
            </div>
          </div>
        </section>

        {/* Pull quote */}
        <section className="w-full py-16">
          <div className="container px-4 md:px-6 max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-2xl p-10 text-center">
              <Quote className="w-8 h-8 text-primary mx-auto mb-5" />
              <p className="text-xl md:text-2xl font-semibold text-foreground leading-relaxed mb-4">
                The CRM hygiene problem is not a training problem. Reps know what to type. They will not type it. The only question worth asking a vendor in 2026 is whether the system writes the update for them.
              </p>
              <p className="text-sm text-muted-foreground">NoQ5 editorial perspective</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-16 bg-muted/30">
          <div className="container px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-foreground text-center">Questions revenue leaders ask</h2>
            <div className="space-y-4">
              {[
                { q: 'What is an AI sales assistant?', a: 'Software that helps revenue teams capture, structure and act on sales activity. The strongest category goes beyond suggestions and autonomously updates the CRM after rep approval, turning calls, meetings and emails into clean pipeline data.' },
                { q: 'What is the difference between an AI copilot and an autonomous AI assistant?', a: 'A copilot suggests; the rep still types. An autonomous assistant captures, drafts and writes back to the CRM with one-tap approval. Copilots reduce friction; autonomous assistants change whether CRM data exists at all.' },
                { q: 'Which AI sales assistant works with HubSpot, Salesforce and Pipedrive?', a: 'NoQ5 ships native integrations with all three. It writes contact, deal and activity updates back to whichever CRM you use, with a full audit trail.' },
                { q: 'How much does an AI sales assistant cost?', a: 'Native CRM assistants are bundled. Enterprise platforms charge per conversation. NoQ5 sells at a single tier of €59 per user per month billed annually, no usage surprises.' },
                { q: 'Is an AI sales assistant GDPR compliant?', a: 'Depends on the vendor. NoQ5 is registered in Ireland, EU-hosted, AES-256-GCM encrypted, with full DPA and audit trail. Always ask vendors for data residency, sub-processors and retention policies.' },
              ].map((f) => (
                <div key={f.q} className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-bold text-foreground mb-2">{f.q}</h3>
                  <p className="text-sm text-foreground/80 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-20">
          <div className="container px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-5 text-foreground">
              See the execution layer in action
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              30-minute demo with a NoQ5 founder. We will pull up your CRM, walk through how voice, email and meeting capture become approved updates, and show you the forecast evidence layer.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl group">
                <Link to="/?scrollTo=demo">
                  Book a Demo
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="font-bold px-8 py-6 text-lg rounded-xl border-2">
                <Link to="/crm-agent">Read the CRM Agent overview</Link>
              </Button>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 text-xs text-muted-foreground max-w-xl mx-auto">
              <div className="flex flex-col items-center gap-1.5"><Mic className="w-4 h-4 text-primary" /> Voice capture</div>
              <div className="flex flex-col items-center gap-1.5"><Mail className="w-4 h-4 text-primary" /> Email capture</div>
              <div className="flex flex-col items-center gap-1.5"><Video className="w-4 h-4 text-primary" /> Meeting capture</div>
            </div>
          </div>
        </section>
      </article>
    </Layout>
  );
};

export default BestAISalesAssistants2026;
